import { z } from 'zod';
import { isValidSiret, normalizeSiret } from '../siret.js';
import { DATE_FORMATS } from '../date.js';
import { PAYMENT_METHODS } from '../receipts.js';
import { ACTIVITY_TYPES, EXPENSE_CATEGORIES } from '../expenses.js';
import { CATALOG_UNITS } from '../quotes.js';
/* ---------------------------------------------------------------- primitifs */
export const siretSchema = z
    .string()
    .transform(normalizeSiret)
    .refine(isValidSiret, { message: 'SIRET invalide (14 chiffres, clé de Luhn incorrecte)' });
export const emailSchema = z.string().trim().toLowerCase().email('Email invalide');
export const passwordSchema = z
    .string()
    .min(10, 'Le mot de passe doit contenir au moins 10 caractères')
    .max(200);
/** Date ISO `YYYY-MM-DD` — format attendu par MariaDB comme par Peppol. */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format YYYY-MM-DD');
export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure attendue au format HH:MM');
/* ---------------------------------------------------------- préférences utilisateur */
export const userPreferencesSchema = z.object({
    dateFormat: z.enum(DATE_FORMATS),
    recurringDescriptions: z
        .array(z.string().trim().min(1).max(255))
        .max(200, 'Au maximum 200 désignations récurrentes'),
    activityType: z.enum(ACTIVITY_TYPES).nullish(),
    autoReminders: z.boolean().optional(),
});
/* --------------------------------------------------------- notifications push */
/** Abonnement d'un téléphone : jeton APNs (iOS, hexadécimal) ou FCM (Android). */
export const pushDeviceSchema = z.object({
    platform: z.enum(['ios', 'android']),
    token: z.string().trim().min(20).max(512).regex(/^[A-Za-z0-9:_\-.]+$/, 'Jeton de notification invalide'),
});
/* --------------------------------------------------------------------- auth */
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({
    token: z.string().min(20).max(200),
    password: passwordSchema,
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Mot de passe requis'),
});
/* ---------------------------------------------------------------- entreprise */
export const CLIENT_KINDS = ['business', 'individual'];
/** Forme juridique de l'entreprise émettrice (déclarations URSSAF à venir). */
export const LEGAL_FORMS = ['micro', 'ei', 'eurl', 'sasu', 'sas', 'sarl', 'sa', 'snc', 'sci', 'association', 'other'];
export const LEGAL_FORM_LABELS = {
    micro: 'Micro-entreprise (auto-entrepreneur)',
    ei: 'Entreprise individuelle (EI, régime réel)',
    eurl: 'EURL',
    sasu: 'SASU',
    sas: 'SAS',
    sarl: 'SARL',
    sa: 'SA',
    snc: 'SNC',
    sci: 'SCI',
    association: 'Association',
    other: 'Autre',
};
/** Régime fiscal du bénéfice : micro (BIC ou BNC selon l'activité), réel ou IS. */
export const TAX_REGIMES = ['micro', 'reel_simplifie', 'reel_normal', 'is'];
export const TAX_REGIME_LABELS = {
    micro: 'Micro (micro-BIC ou micro-BNC)',
    reel_simplifie: 'Réel simplifié (impôt sur le revenu)',
    reel_normal: 'Réel normal (impôt sur le revenu)',
    is: 'Impôt sur les sociétés',
};
/**
 * Forme juridique déduite de la catégorie juridique INSEE (annuaire des
 * entreprises). Une entreprise individuelle (1000) peut être au régime micro ou
 * réel : l'annuaire ne le dit pas, l'utilisateur précise.
 */
export function legalFormFromInsee(code) {
    if (!code)
        return null;
    if (code === '1000')
        return 'ei';
    if (code === '5710')
        return 'sas';
    if (code === '5720')
        return 'sasu';
    if (code === '5498')
        return 'eurl';
    if (code.startsWith('54'))
        return 'sarl';
    if (code.startsWith('55') || code.startsWith('56'))
        return 'sa';
    if (code === '5202')
        return 'snc';
    if (code === '6540')
        return 'sci';
    if (code.startsWith('92'))
        return 'association';
    return 'other';
}
/**
 * Entreprise (émetteur ou client) ou client particulier. Un particulier n'a ni
 * SIRET ni TVA : `legalName` porte ses nom et prénom. La présence du SIRET pour
 * une entreprise est vérifiée par l'API (`assertClientIdentity`), le schéma
 * servant aussi aux mises à jour partielles.
 */
export const companyInputSchema = z.object({
    type: z.enum(['provider', 'client']),
    clientKind: z.enum(CLIENT_KINDS).default('business'),
    legalName: z.string().trim().min(1, 'Nom ou raison sociale requis').max(255),
    siret: siretSchema.nullish(),
    vatNumber: z.string().trim().max(32).nullish(),
    // Identifiant Peppol, ex. `0225:123456789`. Les participants français de
    // l'annuaire officiel utilisent le schéma 0225 (FR:SIRENE, sur le SIREN) —
    // vérifié sur directory.peppol.eu. Le format reste libre : openapi.fr route
    // sur le SIRET et ne consomme pas ce champ.
    peppolId: z
        .string()
        .trim()
        .regex(/^\d{4}:.+$/, 'Identifiant Peppol attendu au format `0225:<identifiant>`')
        .max(64)
        .nullish(),
    phone: z.string().trim().max(32).nullish(),
    // IBAN de l'émetteur. Sans lui, l'API refuse un virement SEPA
    // (« account: can't be blank ») : le moyen de paiement est alors omis.
    iban: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/, 'IBAN invalide')
        .nullish(),
    email: z.string().trim().toLowerCase().email().max(255).nullish(),
    /** Ce que fait l'entreprise : contexte de la génération de devis par IA. Facultatif. */
    activityDescription: z.string().trim().max(1000).nullish(),
    legalForm: z.enum(LEGAL_FORMS).nullish(),
    taxRegime: z.enum(TAX_REGIMES).nullish(),
    /** Impôt payé avec les cotisations URSSAF (régime micro uniquement). */
    versementLiberatoire: z.boolean().optional(),
    /** Code APE (NAF rév. 2), ex. 85.51Z. */
    apeCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^\d{2}\.?\d{2}[A-Z]$/, 'Code APE attendu au format 85.51Z')
        .transform((value) => (value.includes('.') ? value : `${value.slice(0, 2)}.${value.slice(2)}`))
        .nullish(),
    addressLine1: z.string().trim().min(1, 'Adresse requise').max(255),
    addressLine2: z.string().trim().max(255).nullish(),
    postalCode: z.string().trim().min(1, 'Code postal requis').max(20),
    city: z.string().trim().min(1, 'Ville requise').max(100),
    countryCode: z.string().trim().length(2).toUpperCase().default('FR'),
    meta: z.record(z.unknown()).nullish(),
});
/**
 * Inscription : l'entreprise émettrice est créée avec le compte, SIRET
 * obligatoire. Son rôle n'est pas choisi — c'est toujours l'émetteur.
 */
export const registerSchema = z.object({
    email: emailSchema,
    /** Facultatif seulement pour une inscription « avec Google » (vérifié par l'API). */
    password: passwordSchema.optional(),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    // L'émetteur est toujours une entreprise : SIRET obligatoire.
    company: companyInputSchema.omit({ type: true, clientKind: true }).extend({ siret: siretSchema }),
});
/**
 * Mise à jour partielle. `countryCode` est redéfini SANS `.default('FR')` :
 * hérité tel quel, un PATCH ne portant que sur un autre champ réécrirait
 * silencieusement le pays d'une entreprise étrangère.
 */
export const companyUpdateSchema = companyInputSchema.partial().extend({
    countryCode: z.string().trim().length(2).toUpperCase().optional(),
    // Comme countryCode : sans `.default()`, un PATCH partiel ne repasse pas un particulier en entreprise.
    clientKind: z.enum(CLIENT_KINDS).optional(),
    /** Exigé pour changer l'IBAN de l'émetteur : une session volée ne suffit pas. */
    currentPassword: z.string().max(200).optional(),
});
/** Lien « ce n'est pas moi » d'une alerte de changement d'IBAN. */
export const ibanCancelSchema = z.object({ token: z.string().trim().min(20).max(200) });
/* ------------------------------------------------------------------ facture */
/**
 * Refus des saisies plus précises que la colonne SQL correspondante.
 * Sans ce garde-fou, `unit_price` 0,335 est stocké en 0,34 alors que le total
 * de ligne a été calculé sur 0,335 : la facture relue se contredit elle-même.
 */
const maxDecimals = (places) => (value) => {
    const decimals = String(value).split('.')[1]?.length ?? 0;
    return decimals <= places;
};
export const invoiceItemInputSchema = z.object({
    date: isoDateSchema.nullish(),
    time: timeSchema.nullish(),
    description: z.string().trim().min(1, 'Désignation requise'),
    quantity: z
        .number()
        .positive('La quantité doit être positive')
        .max(9_999_999)
        .refine(maxDecimals(3), { message: 'La quantité admet au maximum 3 décimales' }),
    unitPrice: z
        .number()
        .min(0, 'Le prix unitaire ne peut pas être négatif')
        .max(9_999_999)
        .refine(maxDecimals(2), { message: 'Le prix unitaire admet au maximum 2 décimales' }),
    vatRate: z
        .number()
        .min(0)
        .max(100)
        .refine(maxDecimals(2), { message: 'Le taux de TVA admet au maximum 2 décimales' }),
    meta: z.record(z.unknown()).nullish(),
});
/**
 * Aucun montant total n'est accepté depuis le client : le serveur les recalcule
 * intégralement à partir des lignes (cf. computeTotals).
 */
export const invoiceInputSchema = z
    .object({
    providerId: z.string().uuid(),
    clientId: z.string().uuid(),
    invoiceNumber: z.string().trim().max(64).optional(),
    issueDate: isoDateSchema,
    dueDate: isoDateSchema,
    currency: z.string().trim().length(3).toUpperCase().default('EUR'),
    vatExempt: z.boolean().default(false),
    items: z.array(invoiceItemInputSchema).min(1, 'Au moins une ligne est requise'),
    meta: z.record(z.unknown()).nullish(),
})
    .refine((invoice) => invoice.dueDate >= invoice.issueDate, {
    message: "La date d'échéance ne peut pas précéder la date d'émission",
    path: ['dueDate'],
})
    .refine((invoice) => invoice.providerId !== invoice.clientId, {
    message: 'Le prestataire et le client doivent être deux entreprises distinctes',
    path: ['clientId'],
});
export const invoiceUpdateSchema = invoiceInputSchema;
/**
 * Avoir partiel ou total d'une facture transmise : lignes de la facture
 * d'origine et quantité à créditer. Les prix viennent du serveur.
 */
export const creditNoteInputSchema = z.object({
    lines: z
        .array(z.object({
        itemId: z.string().uuid(),
        quantity: z
            .number()
            .positive('La quantité doit être positive')
            .refine(maxDecimals(3), { message: 'La quantité admet au maximum 3 décimales' }),
    }))
        .min(1, 'Choisissez au moins une ligne à créditer'),
});
/* ------------------------------------------------------- brouillon généré (IA) */
/** Demande de brouillon : un client et la consigne en langage naturel. */
export const aiDraftRequestSchema = z.object({
    clientId: z.string().uuid(),
    instructions: z.string().trim().min(10, 'Décrivez la facture à générer').max(2000),
});
/**
 * Brief client pour un devis : un email ou des notes de rendez-vous collés tels
 * quels, donc plus long qu'une consigne de facture.
 */
export const quoteBriefRequestSchema = z.object({
    clientId: z.string().uuid(),
    instructions: z.string().trim().min(10, 'Collez le brief du client ou décrivez la prestation').max(6000),
});
/** Proposition renvoyée par le modèle, revalidée comme une saisie manuelle. */
export const aiDraftProposalSchema = z.object({
    issueDate: isoDateSchema,
    dueDate: isoDateSchema,
    vatExempt: z.boolean(),
    items: z.array(invoiceItemInputSchema.omit({ meta: true })).min(1).max(200),
    explanation: z.string().max(4000),
});
/* ------------------------------------------------------- devis et catalogue */
export const catalogItemInputSchema = z.object({
    label: z.string().trim().min(1, 'Désignation requise').max(255),
    unit: z.enum(CATALOG_UNITS).default('unit'),
    unitPrice: z
        .number()
        .min(0, 'Le prix ne peut pas être négatif')
        .max(9_999_999)
        .refine(maxDecimals(2), { message: 'Le prix admet au maximum 2 décimales' })
        .nullish(),
    vatRate: z.number().min(0).max(100).refine(maxDecimals(2)).default(20),
});
/** Tout le catalogue en un envoi : il se gère comme une liste ordonnée. */
export const catalogInputSchema = z.object({
    items: z.array(catalogItemInputSchema).max(300, 'Au maximum 300 prestations'),
});
const quoteItemInputSchema = invoiceItemInputSchema.extend({
    description: z.string().trim().min(1, 'Désignation requise').max(1000),
});
export const quoteInputSchema = z
    .object({
    providerId: z.string().uuid(),
    clientId: z.string().uuid(),
    issueDate: isoDateSchema,
    validUntil: isoDateSchema,
    startDate: isoDateSchema.nullish(),
    duration: z.string().trim().max(255).nullish(),
    paymentTerms: z.string().trim().max(500).nullish(),
    /** Acompte demandé à la signature, en % du devis (NULL : aucun). */
    depositPercent: z.number().min(1, 'Acompte de 1 % au moins').max(90, 'Acompte de 90 % au plus').multipleOf(0.01).nullish(),
    notes: z.string().trim().max(5000).nullish(),
    currency: z.string().trim().length(3).toUpperCase().default('EUR'),
    vatExempt: z.boolean().default(false),
    items: z.array(quoteItemInputSchema).min(1, 'Au moins une ligne est requise').max(200),
})
    .refine((quote) => quote.validUntil >= quote.issueDate, {
    message: 'La validité ne peut pas précéder la date du devis',
    path: ['validUntil'],
})
    .refine((quote) => quote.providerId !== quote.clientId, {
    message: 'Le prestataire et le client doivent être deux entreprises distinctes',
    path: ['clientId'],
});
/** Proposition de devis par l'IA, revalidée comme une saisie manuelle. */
export const quoteAiProposalSchema = z.object({
    validUntil: isoDateSchema,
    startDate: isoDateSchema.nullish(),
    duration: z.string().trim().max(255).nullish(),
    paymentTerms: z.string().trim().max(500).nullish(),
    notes: z.string().trim().max(5000).nullish(),
    vatExempt: z.boolean(),
    items: z.array(quoteItemInputSchema.omit({ meta: true })).min(1).max(200),
    /** Index des lignes dont le prix est estimé (absent du catalogue et de l'historique). */
    estimatedLines: z.array(z.number().int().min(0)).default([]),
    explanation: z.string().max(4000),
});
/* ------------------------------------------------------------- dépenses */
const moneySchema = (label) => z
    .number()
    .min(0, `${label} ne peut pas être négatif`)
    .max(9_999_999)
    .refine(maxDecimals(2), { message: `${label} admet au maximum 2 décimales` });
export const expenseVatLineSchema = z.object({
    vatRate: z.number().min(0).max(100).refine(maxDecimals(2)),
    baseAmount: moneySchema('La base HT'),
    taxAmount: moneySchema('La TVA'),
});
const centsOf = (value) => Math.round(value * 100);
export const expenseInputSchema = z
    .object({
    purchasedOn: isoDateSchema,
    paidOn: isoDateSchema.nullish(),
    supplierName: z.string().trim().min(1, 'Fournisseur requis').max(255),
    supplierSiret: siretSchema.nullish(),
    supplierVatNumber: z.string().trim().max(32).nullish(),
    documentNumber: z.string().trim().max(64).nullish(),
    description: z.string().trim().min(1, "Nature de l'achat requise").max(500),
    category: z.enum(EXPENSE_CATEGORIES),
    isResale: z.boolean().default(false),
    currency: z.string().trim().regex(/^[A-Za-z]{3}$/, 'Devise invalide').toUpperCase().default('EUR'),
    // Hors euro : montant TTC d'origine et taux ; les montants HT/TVA/TTC restent en euros.
    originalAmount: moneySchema("Le montant d'origine").nullish(),
    exchangeRate: z.number().positive('Le taux de change doit être positif').max(1_000_000).nullish(),
    amountExclTax: moneySchema('Le montant HT').nullish(),
    taxAmount: moneySchema('La TVA').nullish(),
    amountInclTax: moneySchema('Le montant TTC').refine((value) => value > 0, 'Le montant TTC doit être positif'),
    paymentMethod: z.enum(PAYMENT_METHODS).nullish(),
    vatLines: z.array(expenseVatLineSchema).max(10).default([]),
    attachmentId: z.string().uuid().nullish(),
})
    // Même rigueur que les factures émises : au centime près, sinon la dépense se contredit.
    .refine((e) => e.amountExclTax == null || e.taxAmount == null || centsOf(e.amountExclTax) + centsOf(e.taxAmount) === centsOf(e.amountInclTax), { message: 'HT + TVA doit être égal au TTC', path: ['amountInclTax'] })
    .refine((e) => !e.vatLines.length || e.taxAmount == null || e.vatLines.reduce((sum, line) => sum + centsOf(line.taxAmount), 0) === centsOf(e.taxAmount), { message: 'La somme des lignes de TVA doit être égale à la TVA', path: ['vatLines'] })
    .refine((e) => e.currency === 'EUR' || (e.originalAmount != null && e.originalAmount > 0), {
    message: "Indiquez le montant TTC dans la devise d'origine",
    path: ['originalAmount'],
})
    .refine((e) => !e.paidOn || e.paidOn >= e.purchasedOn, {
    message: "Le paiement ne peut pas précéder l'achat",
    path: ['paidOn'],
});
/* ---------------------------------------------------------- livre des recettes */
export const receiptInputSchema = z.object({
    receivedOn: isoDateSchema,
    amount: z
        .number()
        .positive('Le montant doit être positif')
        .max(9_999_999)
        .refine(maxDecimals(2), { message: 'Le montant admet au maximum 2 décimales' }),
    paymentMethod: z.enum(PAYMENT_METHODS),
    clientName: z.string().trim().max(255).nullish(),
    description: z.string().trim().min(1, 'Nature de la vente ou de la prestation requise').max(500),
    invoiceId: z.string().uuid().nullish(),
});
/* ------------------------------------------------------- expert-comptable */
export const accountantInviteSchema = z.object({ email: emailSchema });
export const accountantAcceptSchema = z.object({
    password: z.string().min(1, 'Mot de passe requis').max(200),
    firstName: z.string().trim().max(100).nullish(),
    lastName: z.string().trim().max(100).nullish(),
    firmName: z.string().trim().max(255).nullish(),
});
/** Compte du plan comptable général : classe 6 (charges) ou 7 (produits), 6 à 8 chiffres. */
export const ledgerOverrideSchema = z.object({
    entryKey: z.string().regex(/^(invoice|expense):[A-Za-z0-9._\/-]{1,90}$/, 'Écriture inconnue'),
    accountFrom: z.string().regex(/^[67]\d{5,7}$/, 'Compte de charge (6…) ou de produit (7…) attendu'),
    accountTo: z.string().regex(/^[67]\d{5,7}$/, 'Compte de charge (6…) ou de produit (7…) attendu'),
    accountLabel: z.string().trim().max(100).nullish(),
});
/* ------------------------------------------------------------------ support */
export const SUPPORT_CATEGORIES = ['account', 'invoice', 'peppol', 'billing', 'ai', 'mobile', 'other'];
export const SUPPORT_CATEGORY_LABELS = {
    account: 'Compte et connexion',
    invoice: 'Factures et devis',
    peppol: 'Envoi, réseau Peppol, réception',
    billing: 'Abonnement et paiement',
    ai: "Assistant IA",
    mobile: 'Applications mobiles',
    other: 'Autre question',
};
/** Ticket d'assistance (centre d'aide). `website` est un piège à robots : rempli, le ticket est ignoré. */
export const supportTicketSchema = z.object({
    name: z.string().trim().min(1, 'Votre nom').max(120),
    email: emailSchema,
    category: z.enum(SUPPORT_CATEGORIES),
    subject: z.string().trim().min(3, 'Objet trop court').max(150),
    message: z.string().trim().min(20, 'Décrivez votre demande en quelques phrases (20 caractères minimum)').max(5000),
    website: z.string().max(200).optional(),
});
/* -------------------------------------------------- devis signé sur place */
/** PNG en data URL, 300 Ko au plus (une signature au doigt pèse 10 à 40 Ko). */
const SIGNATURE_MAX_CHARS = 400_000;
/**
 * Signature du client sur l'écran de l'artisan (chantier). Preuve : nom, mention
 * « Bon pour accord » cochée, tracé, et position si le téléphone la donne.
 */
export const quoteOnSiteAcceptSchema = z.object({
    name: z.string().trim().min(2, 'Nom du signataire requis').max(255),
    agreed: z.literal(true, { errorMap: () => ({ message: 'Le client doit cocher « Bon pour accord »' }) }),
    signature: z
        .string()
        .max(SIGNATURE_MAX_CHARS, 'Signature trop volumineuse')
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Signature invalide'),
    /** Adresse où envoyer la copie signée, si le client en donne une autre que celle de la fiche. */
    email: emailSchema.nullish(),
    location: z
        .object({ latitude: z.number().min(-90).max(90), longitude: z.number().min(-180).max(180), accuracy: z.number().min(0).max(100_000) })
        .nullish(),
});
/** Relance déclenchée à la main par l'artisan. */
export const manualReminderSchema = z.object({
    targetType: z.enum(['quote', 'invoice']),
    targetId: z.string().uuid(),
});
/* ---------------------------------------------------- planning et réservation */
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure au format HH:MM');
export const bookingSettingsSchema = z
    .object({
    enabled: z.boolean(),
    baseAddress: z.string().trim().max(255).nullish(),
    radiusKm: z.number().int().min(1).max(200),
    minNoticeHours: z.number().int().min(0).max(24 * 14),
    bufferMinutes: z.number().int().min(0).max(240),
    horizonDays: z.number().int().min(1).max(120),
    weeklyHours: z
        .array(z.object({ day: z.number().int().min(1).max(7), start: hhmm, end: hhmm }))
        .max(28)
        .refine((ranges) => ranges.every((range) => range.start < range.end), 'Une plage doit finir après son début'),
    services: z
        .array(z.object({
        id: z.string().uuid().nullish(),
        label: z.string().trim().min(2, "Nom de l'intervention").max(120),
        durationMinutes: z.number().int().min(15, '15 minutes au moins').max(8 * 60),
        priceEur: z.number().min(0).max(100_000).nullish(),
    }))
        .max(20),
    closures: z
        .array(z.object({ startsOn: isoDateSchema, endsOn: isoDateSchema, label: z.string().trim().max(120).nullish() }))
        .max(50)
        .refine((list) => list.every((closure) => closure.startsOn <= closure.endsOn), 'Une fermeture doit finir après son début'),
})
    .refine((settings) => !settings.enabled || settings.services.length > 0, { message: 'Ajoutez au moins un type d’intervention', path: ['services'] })
    .refine((settings) => !settings.enabled || settings.weeklyHours.length > 0, { message: 'Ajoutez au moins une plage horaire', path: ['weeklyHours'] });
export const bookingSlotsRequestSchema = z.object({
    serviceId: z.string().uuid(),
    address: z.string().trim().min(5, 'Adresse du chantier').max(255),
});
export const bookingRequestSchema = bookingSlotsRequestSchema.extend({
    date: isoDateSchema,
    time: hhmm,
    name: z.string().trim().min(2, 'Votre nom').max(160),
    email: emailSchema,
    phone: z.string().trim().regex(/^[+0-9 .()-]{8,20}$/, 'Numéro de téléphone invalide'),
    description: z.string().trim().max(2000).nullish(),
    /** Piège à robots (invisible) : rempli, la demande est ignorée. */
    website: z.string().max(200).optional(),
});
/** Rendez-vous saisi par l'artisan (appel, message) : il peut déborder de ses plages. */
export const manualAppointmentSchema = z.object({
    serviceLabel: z.string().trim().min(2, 'Objet du rendez-vous').max(120),
    date: isoDateSchema,
    time: hhmm,
    durationMinutes: z.number().int().min(15).max(12 * 60),
    clientName: z.string().trim().min(2, 'Nom du client').max(160),
    clientPhone: z.string().trim().regex(/^[+0-9 .()-]{8,20}$/, 'Numéro de téléphone invalide').nullish().or(z.literal('')),
    clientEmail: emailSchema.nullish().or(z.literal('')),
    address: z.string().trim().min(3, 'Adresse du rendez-vous').max(255),
    description: z.string().trim().max(2000).nullish(),
});
//# sourceMappingURL=index.js.map